import { useEffect, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { getCustomerContext, type CustomerSubscription } from '../api/customerClient';
import { useAuthArgs } from '../hooks/useAuthArgs';

interface ProductItem {
  name: string;
  active: boolean;
  status: string;
}

interface CaseItem {
  id: string;
  title: string;
  status?: string;
  priority?: string;
  channel: string;
  updated: string;
  description?: string;
}

/**
 * Dashboard shown after the user signs in.
 * Displays: Your Products (left), Open Cases + New Case button (right), Previous Cases (bottom).
 * All data is currently dummy/mock.
 */
export function DashboardPage({ onNewCase }: { onNewCase: () => void }) {
  const [selectedCase, setSelectedCase] = useState<CaseItem | null>(null);
  const { isSignedIn, account } = useAuth();
  const getAuthArgs = useAuthArgs();
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [latency, setLatency] = useState<{ roundTripMs: number; shopperLatencyMs: number } | null>(null);

  useEffect(() => {
    // Signed out: clear any previous user's data and reset the loading flag so a later sign-in
    // starts fresh (and we never leave a stale "Loading…"/other-user's products on screen).
    if (!isSignedIn || !account?.homeAccountId) {
      setProducts([]);
      setLatency(null);
      setProductsLoaded(false);
      return;
    }
    let cancelled = false;
    setProductsLoaded(false);

    void (async () => {
      try {
        const auth = await getAuthArgs();
        if (!auth) {
          throw new Error('Unable to acquire an API token for customer context.');
        }

        const context = await getCustomerContext(auth);
        if (cancelled) return;
        setProducts(context.subscriptions.map(toProductItem));
        setLatency({ roundTripMs: context.roundTripMs, shopperLatencyMs: context.shopperLatencyMs });
      } catch (err) {
        if (cancelled) return;
        // Best-effort: clear stale data and let the products panel fall back to the empty state.
        setProducts([]);
        setLatency(null);
        // eslint-disable-next-line no-console
        console.error('[dashboard] customer context fetch failed:', err);
      } finally {
        if (!cancelled) setProductsLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [isSignedIn, account?.homeAccountId, getAuthArgs]);

  return (
    <div style={styles.container}>
      <div style={styles.grid}>
        {/* Left sidebar — Your Products */}
        <aside style={styles.productsCard}>
          <div style={styles.productsHeader}>
            <h3 style={styles.sectionTitle}>YOUR PRODUCTS</h3>
            {latency && (
              <span
                style={styles.latencyBadge}
                title={`End-to-end round-trip: ${formatSeconds(latency.roundTripMs)} · Shopper service read: ${formatSeconds(latency.shopperLatencyMs)}`}
              >
                ⏱ {formatSeconds(latency.roundTripMs)} · Shopper {formatSeconds(latency.shopperLatencyMs)}
              </span>
            )}
          </div>
          {products.length > 0 ? (
            <ul style={styles.productList}>
              {products.map((p, idx) => (
                <li key={`${p.name}-${idx}`} style={styles.productItem}>
                  <span style={{ ...styles.dot, background: p.active ? '#22c55e' : '#9ca3af' }} />
                  <div>
                    <div style={styles.productName}>{p.name}</div>
                    <div style={styles.productStatus}>{p.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p style={styles.productsEmpty}>
              {productsLoaded ? 'No products found.' : 'Loading your products…'}
            </p>
          )}
        </aside>

        {/* Right content — Open Cases */}
        <section style={styles.mainContent}>
          <div style={styles.openCasesHeader}>
            <div>
              <h2 style={styles.heading}>Open Cases</h2>
              <span style={styles.badge}>{dummyOpenCases.length} active</span>
            </div>
            <button onClick={onNewCase} className="hover-new-case" style={styles.newCaseButton}>+ New Case</button>
          </div>
          <hr style={styles.divider} />
          <div style={styles.casesGrid}>
            {dummyOpenCases.map((c) => (
              <div key={c.id} className="hover-case-card" style={styles.caseCard} onClick={() => setSelectedCase(c)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCase(c); } }} role="button" tabIndex={0}>
                <div style={styles.caseHeader}>
                  <span style={styles.caseId}>{c.id}</span>
                  <span style={{
                    ...styles.statusBadge,
                    background: c.status === 'Open' ? '#dcfce7' : '#fef9c3',
                    color: c.status === 'Open' ? '#166534' : '#854d0e',
                  }}>
                    {c.status}
                  </span>
                </div>
                <p style={styles.caseTitle}>{c.title}</p>
                <p style={styles.caseMeta}>{c.priority} · {c.channel} · Updated {c.updated}</p>
              </div>
            ))}
          </div>

          {/* Previous Cases */}
          <div style={styles.previousSection}>
            <div style={styles.previousHeader}>
              <h2 style={styles.heading}>Previous Cases</h2>
              <span style={styles.resolvedCount}>{dummyPreviousCases.length} resolved</span>
            </div>
            <hr style={styles.divider} />
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Case</th>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Channel</th>
                  <th style={styles.th}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {dummyPreviousCases.map((c) => (
                  <tr key={c.id} className="hover-case-row" onClick={() => setSelectedCase(c)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedCase(c); } }} tabIndex={0} style={{ cursor: 'pointer' }}>
                    <td style={styles.td}>{c.id}</td>
                    <td style={styles.td}>{c.title}</td>
                    <td style={styles.td}>{c.channel}</td>
                    <td style={styles.td}>{c.updated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* Case detail modal */}
      {selectedCase && (
        <div style={styles.overlay} onClick={() => setSelectedCase(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={`Case details for ${selectedCase.id}`}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{selectedCase.id}</h2>
              <button onClick={() => setSelectedCase(null)} style={styles.modalClose} aria-label="Close case details">×</button>
            </div>
            <hr style={styles.divider} />
            <div style={styles.modalBody}>
              <div style={styles.modalRow}>
                <span style={styles.modalLabel}>Title</span>
                <span>{selectedCase.title}</span>
              </div>
              {selectedCase.status && (
                <div style={styles.modalRow}>
                  <span style={styles.modalLabel}>Status</span>
                  <span style={{
                    ...styles.statusBadge,
                    background: selectedCase.status === 'Open' ? '#dcfce7' : '#fef9c3',
                    color: selectedCase.status === 'Open' ? '#166534' : '#854d0e',
                  }}>{selectedCase.status}</span>
                </div>
              )}
              {selectedCase.priority && (
                <div style={styles.modalRow}>
                  <span style={styles.modalLabel}>Priority</span>
                  <span>{selectedCase.priority}</span>
                </div>
              )}
              <div style={styles.modalRow}>
                <span style={styles.modalLabel}>Channel</span>
                <span>{selectedCase.channel}</span>
              </div>
              <div style={styles.modalRow}>
                <span style={styles.modalLabel}>Last Updated</span>
                <span>{selectedCase.updated}</span>
              </div>
              <div style={styles.modalRow}>
                <span style={styles.modalLabel}>Description</span>
                <span>{selectedCase.description ?? 'No additional details available. This will be populated from the case management API.'}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Product mapping ---

function formatSeconds(milliseconds: number): string {
  return `${(milliseconds / 1000).toFixed(1)}s`;
}

function toProductItem(sub: CustomerSubscription): ProductItem {
  const name = sub.skuTitle || sub.productName || 'Subscription';
  const active = (sub.state ?? '').toLowerCase() === 'active';
  const parts = [sub.state ?? (active ? 'Active' : 'Inactive')];
  if (sub.isTrial) parts.push('Trial');
  if (sub.renewalDateUtc) {
    const renews = new Date(sub.renewalDateUtc);
    if (!Number.isNaN(renews.getTime())) {
      parts.push(`${sub.autoRenewEnabled ? 'Renews' : 'Ends'} ${renews.toLocaleDateString()}`);
    }
  }
  return { name, active, status: parts.join(' · ') };
}

// --- Dummy data ---

const dummyOpenCases = [
  { id: 'CS-24091', title: 'Cannot download monthly invoice bundle', status: 'Open', priority: 'High priority', channel: 'Chat', updated: '5m ago', description: 'User reports that the monthly invoice PDF download button returns a 404 error since June 2026. Affecting the billing portal under Account > Invoices. Support is investigating with the billing team.' },
  { id: 'CS-24003', title: 'SSO login redirect loop for EU users', status: 'Pending', priority: 'High priority', channel: 'Email', updated: '42m ago', description: 'Multiple EU-region users experience an infinite redirect loop when attempting SSO login via Azure AD. Issue began after the latest tenant policy update. Escalated to identity team for review.' },
];

const dummyPreviousCases = [
  { id: 'CS-23870', title: 'Need API usage export for Q1 audit', channel: 'Phone', updated: '1d ago', description: 'Customer requested a CSV export of API call logs for January–March 2026 for internal compliance audit. Export was generated and sent via secure link. Case resolved.' },
  { id: 'CS-23552', title: 'Unable to add secondary billing contact', channel: 'Email', updated: '4d ago', description: 'User could not add a second billing contact due to a validation error on the email field. Root cause was a backend regex issue. Fixed in patch v2.4.1. Customer confirmed resolution.' },
];

// --- Styles ---

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px 24px',
    maxWidth: 1200,
    margin: '0 auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '260px 1fr',
    gap: 24,
    alignItems: 'start',
  },
  productsCard: {
    background: 'white',
    borderRadius: 12,
    padding: '24px',
    border: '1px solid #e5e7eb',
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 1,
    color: '#374151',
    margin: '0 0 16px',
  },
  productsHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
    flexWrap: 'wrap',
  },
  latencyBadge: {
    fontSize: 11,
    fontWeight: 600,
    color: '#6b7280',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    marginBottom: 16,
  },
  productList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  productItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
  },
  productName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#111827',
  },
  productStatus: {
    fontSize: 12,
    color: '#6b7280',
  },
  productsEmpty: {
    fontSize: 13,
    color: '#6b7280',
    margin: 0,
  },
  mainContent: {
    background: 'white',
    borderRadius: 12,
    padding: '28px 32px',
    border: '1px solid #e5e7eb',
  },
  openCasesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    display: 'inline',
  },
  badge: {
    marginLeft: 10,
    fontSize: 14,
    color: '#6b7280',
  },
  newCaseButton: {
    background: '#0078d4',
    color: 'white',
    border: 'none',
    padding: '10px 22px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  divider: {
    border: 'none',
    borderTop: '1px solid #e5e7eb',
    margin: '16px 0',
  },
  casesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 16,
  },
  caseCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: '16px 20px',
    background: '#fafafa',
    cursor: 'pointer',
    transition: 'box-shadow 0.15s',
  },
  caseHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  caseId: {
    fontSize: 15,
    fontWeight: 700,
    color: '#111827',
  },
  statusBadge: {
    fontSize: 12,
    fontWeight: 600,
    padding: '3px 10px',
    borderRadius: 999,
  },
  caseTitle: {
    fontSize: 14,
    color: '#374151',
    margin: '4px 0',
  },
  caseMeta: {
    fontSize: 12,
    color: '#9ca3af',
    margin: 0,
  },
  previousSection: {
    marginTop: 32,
  },
  previousHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  resolvedCount: {
    fontSize: 14,
    color: '#6b7280',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    fontSize: 13,
    fontWeight: 600,
    color: '#6b7280',
    padding: '10px 12px',
    borderBottom: '1px solid #e5e7eb',
  },
  td: {
    fontSize: 14,
    color: '#374151',
    padding: '12px 12px',
    borderBottom: '1px solid #f3f4f6',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: 12,
    width: '100%',
    maxWidth: 520,
    padding: '24px 28px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
    color: '#111827',
  },
  modalClose: {
    background: 'transparent',
    border: 'none',
    fontSize: 24,
    cursor: 'pointer',
    color: '#6b7280',
    padding: '4px 8px',
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 14,
  },
  modalRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  modalLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: 0.5,
  },
};
